import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Modal } from 'react-bootstrap';

import type { IModalConfig } from './types';

import { PanelLabel } from '@/components/common';
import { media, modals } from '@/styles/theme';
import { CrossIcon } from '@/assets/icons';

interface IModalContext {
  openModal: (config: IModalConfig) => void;
  closeModal: () => void;
  __setHandles: (open: IModalContext['openModal'], close: IModalContext['closeModal']) => void;
}

const initialModalContext: IModalContext = {
  openModal: () => {
    throw new Error('Modal handle is uninitialized.');
  },
  closeModal: () => {
    throw new Error('Modal handle is uninitialized.');
  },
  __setHandles: () => {
    throw new Error('Modal handle is uninitialized.');
  },
};

const ModalContext = React.createContext(initialModalContext);

interface IModalProviderProps {
  children: React.ReactNode;
}

const ModalProvider = ({ children }: IModalProviderProps): React.ReactElement => {
  const [modalHandles, setModalHandles] = useState(initialModalContext);
  const contextValue = useMemo<IModalContext>(
    () => ({
      openModal: modalHandles.openModal,
      closeModal: modalHandles.closeModal,
      __setHandles: (open, close) => {
        setModalHandles(handles => ({
          ...handles,
          openModal: open,
          closeModal: close,
        }));
      },
    }),
    [modalHandles.closeModal, modalHandles.openModal],
  );

  return <ModalContext.Provider value={contextValue}>{children}</ModalContext.Provider>;
};

interface IUseModalHook {
  openModal: IModalContext['openModal'];
  closeModal: IModalContext['closeModal'];
}

const useModal = (): IUseModalHook => {
  const { openModal, closeModal } = useContext(ModalContext);

  return {
    openModal,
    closeModal,
  };
};

const initialModalConfig = {
  title: '',
  content: <></>,
};

const ModalContainer = (): React.ReactElement => {
  const { __setHandles } = useContext(ModalContext);
  const [isOpen, setOpen] = useState(false);
  const [modalConfig, setModalConfig] = useState<IModalConfig>(initialModalConfig);

  const openModal = useCallback(
    (config: IModalConfig) => {
      setModalConfig(config);
      setOpen(true);
    },
    [setModalConfig, setOpen],
  );

  const closeModal = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  useEffect(() => {
    __setHandles(openModal, closeModal);
  }, [__setHandles, closeModal, openModal]);

  return (
    <StyledModal size={modalConfig.size} show={isOpen} onHide={closeModal} centered className={`${modalConfig.size}`}>
      <Modal.Header>
        <Header>
          <PanelLabel linesPosition={modalConfig.linesPosition ?? 'top'}>{modalConfig.title}</PanelLabel>
          <CloseButton onClick={closeModal} className={`${modalConfig.disableClose && 'disabled'}`} />
        </Header>
        {modalConfig.subHeader && <SubHeader>{modalConfig.subHeader}</SubHeader>}
      </Modal.Header>
      {modalConfig.content}
    </StyledModal>
  );
};

export { useModal, ModalProvider, ModalContainer };

const StyledModal = styled(Modal)`
  .modal-content {
    padding: 0.5rem 0.5rem 0;
  }

  .modal-header {
    border-bottom: none;
    padding-top: 1.5rem;
    flex-direction: column;
  }

  .panel-label {
    margin-bottom: 0 !important;

    .color-accent {
      display: none !important;

      ${media.laptop} {
        display: flex !important;
      }
    }
  }

  ${media.laptop} {
    &.s {
      .modal-dialog {
        max-width: ${modals.width.tablet.s};
      }
    }

    &.m {
      .modal-dialog {
        max-width: ${modals.width.tablet.m};
      }
    }

    &.l {
      .modal-dialog {
        max-width: ${modals.width.tablet.l};
      }
    }
  }

  ${media.desktop} {
    &.s {
      .modal-dialog {
        max-width: ${modals.width.desktop.s};
      }
    }

    &.m {
      .modal-dialog {
        max-width: ${modals.width.desktop.m};
      }
    }

    &.l {
      .modal-dialog {
        max-width: ${modals.width.desktop.l};
      }
    }
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

const SubHeader = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const CloseButton = styled.div`
  height: 12px;
  width: 12px;
  background-image: url(${CrossIcon});
  background-size: 12px;
  background-position: center;
  background-repeat: no-repeat;
  cursor: pointer;

  &.disabled {
    user-select: none;
    pointer-events: none;
    cursor: default;
  }
`;
